import * as React from "react";
import { AuditContextProvider, useAuditContext } from "~/common/audit";

type RendererState = (children: React.ReactNode) => void;
const RendererContext = React.createContext<RendererState>(null);

type Options = {
    /**
     * Since modalPopup is rendered on the body, it's outside the original ModalRendererContext.
     * There are cases where we need to display a next modal from the parent, closing the parent one,
     * that's why we may want to pass the renderer context further.
     */
    proxyRendererContext?: boolean;
};

export const useRenderModal = <T extends {}>(
    getModal: (close: F<void>, props?: T) => React.ReactElement,
    { proxyRendererContext }: Options = {}
) => {
    const render = React.useContext(RendererContext);
    const close = () => render(null);
    const currentContext = useAuditContext();

    return (props?: T) =>
        render(
            <AuditContextProvider location={currentContext.location}>
                <CloseModalContext.Provider value={{ close }}>
                    {proxyRendererContext ? (
                        <RendererContext.Provider value={render}>{getModal(close, props)}</RendererContext.Provider>
                    ) : (
                        getModal(close, props)
                    )}
                </CloseModalContext.Provider>
            </AuditContextProvider>
        );
};

const CloseModalContext = React.createContext<{ close: F<void> }>(null);
export const useCloseModal = () => {
    const ctx = React.useContext(CloseModalContext);
    return ctx.close;
};

/*
 * Used to render some content from the menu which is unmouned when clicked.
 */
export const ModalRendererContext: React.FC = ({ children }) => {
    const [content, setContent] = React.useState<React.ReactNode>(null);

    return (
        <>
            <RendererContext.Provider value={setContent}>{children}</RendererContext.Provider>
            {content}
        </>
    );
};

export const withModalProvider =
    <TProps extends {}>(Component: React.ComponentType<TProps>) =>
    (props: TProps) =>
        (
            <ModalRendererContext>
                <Component {...props} />
            </ModalRendererContext>
        );
